import { useCallback, useEffect, useState } from "react";
import { create, connect, iAmReady, getStoreInfo, NexoClient, StoreInfoResponse } from "@tiendanube/nexo";
import { CheckCircle2, ExternalLink } from "lucide-react";
import CustomButton from "components/CustomButton";
import StatCard from "components/StatCard";
import StatusBadge from "components/admin/StatusBadge";
import { Skeleton } from "components/ui/skeleton";
import { verResumoEmbedNuvemshop, NuvemshopResumoEmbed } from "services/api";
import { RegrasFreteManager } from "pages/nuvemshop/RegrasFreteManager";

const NUVEMSHOP_APP_ID = process.env.REACT_APP_NUVEMSHOP_APP_ID || "";

type NexoStatus = "connecting" | "ready" | "error";

function carrierStatus(carrier: NuvemshopResumoEmbed["carrier"]) {
  if (!carrier) return { tone: "slate" as const, label: "Não foi possível verificar" };
  if (!carrier.registered) return { tone: "yellow" as const, label: "Ainda não registrado" };
  if (carrier.active === false) return { tone: "yellow" as const, label: "Registrado, mas inativo" };
  if (carrier.active === true) return { tone: "green" as const, label: "Ativo" };
  return { tone: "slate" as const, label: "Registrado" };
}

export function NuvemshopEmbedded() {
  const [nexoStatus, setNexoStatus] = useState<NexoStatus>("connecting");
  const [storeInfo, setStoreInfo] = useState<StoreInfoResponse | null>(null);
  const [resumo, setResumo] = useState<NuvemshopResumoEmbed | null>(null);
  const [resumoLoading, setResumoLoading] = useState(false);

  const carregarResumo = useCallback(async (storeId: string) => {
    setResumoLoading(true);
    try {
      const { data } = await verResumoEmbedNuvemshop(storeId);
      setResumo(data);
    } catch {
      setResumo(null);
    } finally {
      setResumoLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!NUVEMSHOP_APP_ID) {
      setNexoStatus("error");
      return;
    }

    let alive = true;
    const nexo: NexoClient = create({ clientId: NUVEMSHOP_APP_ID });

    connect(nexo)
      .then(async () => {
        if (!alive) return;
        iAmReady(nexo);
        setNexoStatus("ready");
        try {
          const info = await getStoreInfo(nexo);
          if (!alive) return;
          setStoreInfo(info);
          if (info?.id) carregarResumo(info.id);
        } catch {
          // segue sem o nome da loja — não é crítico
        }
      })
      .catch(() => {
        if (alive) setNexoStatus("error");
      });

    return () => {
      alive = false;
    };
  }, [carregarResumo]);

  const status = carrierStatus(resumo?.carrier ?? null);

  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-6 py-10">
      <div className="w-full max-w-2xl text-center">
        <img src="/images/intrex_logo.png" alt="Intrex" className="h-10 mx-auto mb-6" />

        {nexoStatus === "connecting" && (
          <div className="space-y-3 max-w-md mx-auto">
            <Skeleton className="h-5 w-40 mx-auto" />
            <Skeleton className="h-4 w-56 mx-auto" />
          </div>
        )}

        {nexoStatus === "ready" && (
          <div>
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100 text-green-600">
              <CheckCircle2 className="h-7 w-7" />
            </div>
            <h2 className="text-lg font-bold text-slate-900 mb-1">
              {storeInfo?.name ? `Olá, ${storeInfo.name}!` : "Intrex conectado"}
            </h2>
            <p className="text-sm text-slate-500 mb-5">
              Fretes internacionais com desconto UPS/FedEx direto no checkout da sua loja.
            </p>

            {resumoLoading && (
              <div className="flex flex-wrap items-center justify-center gap-2 mb-6">
                <Skeleton className="h-6 w-32 rounded-md" />
              </div>
            )}

            {!resumoLoading && resumo?.connected && (
              <>
                <div className="flex flex-wrap items-center justify-center gap-2 mb-6">
                  <StatusBadge label={`Frete internacional: ${status.label}`} tone={status.tone} />
                </div>

                {resumo.stats && (
                  <div className="grid grid-cols-3 gap-3 mb-6 text-left">
                    <StatCard title="Cotações" subtitle="Total" value={resumo.stats.totalCotacoes} />
                    <StatCard title="Em andamento" subtitle="Envios" value={resumo.stats.emAndamento} />
                    <StatCard title="Entregues" subtitle="Envios" value={resumo.stats.entregues} />
                  </div>
                )}

                {storeInfo?.id && (
                  <div className="mb-6">
                    <RegrasFreteManager storeId={storeInfo.id} />
                  </div>
                )}
              </>
            )}

            {!resumoLoading && resumo && !resumo.connected && (
              <p className="text-xs text-slate-400 mb-6">
                Ainda não encontramos o vínculo dessa loja com uma conta Intrex.
              </p>
            )}

            <a href="/login" target="_blank" rel="noopener noreferrer">
              <CustomButton
                label="Abrir painel Intrex"
                icon={<ExternalLink className="h-4 w-4" />}
                iconDirection="right"
                className="w-full max-w-md"
              />
            </a>
          </div>
        )}

        {nexoStatus === "error" && (
          <div className="max-w-md mx-auto">
            <h2 className="text-lg font-bold text-slate-900 mb-1">Intrex</h2>
            <p className="text-sm text-slate-500 mb-6">
              Não foi possível conectar automaticamente com este painel agora, mas sua loja continua
              funcionando normalmente. Acesse o painel completo pelo link abaixo.
            </p>
            <a href="/login" target="_blank" rel="noopener noreferrer">
              <CustomButton
                label="Abrir painel Intrex"
                icon={<ExternalLink className="h-4 w-4" />}
                iconDirection="right"
                className="w-full"
              />
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
